export interface StoryChapter {
  key: string
  title: string
  keywords: string[]
  content: string
}

export const storyOverview = `From Clay to Code: A Developer Story

Before I wrote my first line of code, I spent nearly a decade at
Prairie Hill Learning Center, a farm-based Montessori school.
That experience shaped everything about how I approach engineering.

The thread connecting all of it: the most powerful use of technology
isn't replacing human thinking. It's amplifying our natural
problem-solving processes.`

export const storyChapters: StoryChapter[] = [
  {
    key: 'clay',
    title: 'From Clay to Code',
    keywords: ['montessori', 'origin', 'clay', 'teaching'],
    content: `Before I ever wrote my first line of code, I spent nearly a decade at
Prairie Hill Learning Center, a farm-based Montessori school for ages
18 months to 14 years.

I learned that people learn best when they're intrinsically motivated,
when the work emerges from genuine curiosity, and when different working
styles are respected rather than flattened into uniformity. These aren't
just educational principles. They're human principles that apply to
engineering teams too.

The same instinct that made me good with kids: meeting people where they
are, breaking complex things into understandable pieces, finding the
interesting problem inside the frustrating one, turned out to be exactly
what engineering demands.`,
  },
  {
    key: 'payments',
    title: 'The Path Through Payments',
    keywords: ['payments', 'cardflight', 'paymentspring'],
    content: `At CardFlight, I helped build core payment processing libraries for a
mobile payment SDK connecting iOS and Android devices to backend
services for processing, settlement, and reporting. I helped establish
engineering quality practices: RSpec suites, SimpleCov coverage, RuboCop
standards. I also worked on processor certifications with Vantiv/Worldpay
and First Data, where "it works on my machine" is meaningless when
handling real money.

PaymentSpring took me into infrastructure. AWS SAM, Lambda functions,
Step Functions state machines, CloudFormation infrastructure-as-code.
Shipping revenue-impacting features at an early-stage startup.`,
  },
  {
    key: 'taxjar',
    title: 'TaxJar: Making Problems Disappear',
    keywords: ['taxjar', 'tax', 'stripe', 'audit', 'automation'],
    content: `TaxJar became my favorite work environment. Automatic sales tax filing
for thousands of businesses. 50K+ users, billions in annual transactions.

I led an automation project that replaced a complex, error-prone manual
workflow for handling state audit data requests. Engineers had to manually
execute commands across Rails console, PostgreSQL, and AWS CLI. I turned
it into a single button click on an admin dashboard.

Results: eliminated 100+ developer hours annually, reduced process time
from 2+ hours to 10-15 minutes, eliminated false positive data retrieval
errors entirely, and met enterprise-grade PII security requirements
during the Stripe acquisition.`,
  },
  {
    key: 'huntress',
    title: 'Huntress: Lessons in Leadership',
    keywords: ['huntress', 'security', 'microsoft', 'sre'],
    content: `At Huntress I worked on the Microsoft 365 integration for enterprise
security monitoring, processing events across thousands of organizations.
Maintained 99.9%+ uptime. Did SRE rotation with production monitoring,
incident response, root cause analysis.

The environment taught me something important: the worst challenges
aren't technical. They're political ones where the constraint isn't
complexity but poor communication and leadership that treats engineers
as code-writing machines rather than problem-solving professionals.

I was laid off in January 2024. And that's when things got interesting.`,
  },
  {
    key: 'epiphany',
    title: 'The Epiphany with Clay',
    keywords: ['epiphany', 'rubber', 'ducky', 'duck', 'transcription'],
    content: `Out of work, I went back to building Rails apps. Then came an epiphany
while struggling with writing technical articles, triggered by a memory
from Montessori days: preparing parent-teacher conferences and feeling
stuck until I had a breakthrough while molding clay.

Just like clay, I could start with raw material and shape it later. This
led me to build a transcription tool, then feed transcripts into an LLM.

That first connection between transcription and AI text processing became
Rubber Ducky, a Rails app that enhanced rubber duck debugging by providing
structured output from free-form problem-solving conversations.

I just gave the duck a brain.`,
  },
  {
    key: 'cancode',
    title: 'Building can.code',
    keywords: ['cancode', 'can.code', 'studio', 'ecosystem'],
    content: `What followed was an explosion of building. Not just one app but an
entire ecosystem. 80+ projects in two years.

Four production Next.js apps with MongoDB, WebSocket streaming, real-time
data processing. A production Go CLI at 13K+ lines with SQLite, concurrent
processing, API server. 90+ React components with 33+ custom hooks.

The Rubber Ducky concept evolved into continuous iteration on
speak-transcribe-respond workflows, templating custom agents for
interviews, story writing, business planning, project management, and
development tools. Crucially, it led to solutions for agent memory and
session management for iterative LLM requests.`,
  },
  {
    key: 'ai',
    title: 'How AI Changed My Practice',
    keywords: ['ai', 'practice', 'workflow'],
    content: `I rarely write code by hand anymore. I focus on architectural thinking,
system design, and guiding AI tools to implement solutions. My role
shifted from typing code to problem-solving, making trade-offs, and
designing systems.

When I use AI: implementation tasks, boilerplate, exploring unfamiliar
patterns, technical writing.

When I don't: high-level architecture, security-sensitive code,
performance-critical optimizations, debugging complex production issues.

This represents another abstraction layer, similar to how we moved from
assembly to high-level languages, from manual memory management to
garbage collection. Each time, we discovered we could focus on
higher-level problems.`,
  },
  {
    key: 'opinion',
    title: 'The Controversial Opinion',
    keywords: ['opinion', 'controversial', 'future'],
    content: `We are moving toward a world where we don't need to care as much about
what the code looks like, only what problems it solves, and this
represents better engineering, not worse.

The real skill becomes knowing what problems to solve and how to validate
that your solution actually works. Testing, monitoring, security
practices, deployment strategies; these engineering fundamentals become
more important, not less.

The opportunity is in teaching proper engineering standards for
AI-assisted development, not in rejecting the tools entirely.`,
  },
  {
    key: 'looking',
    title: 'What I\'m Looking For',
    keywords: ['looking', 'seeking', 'want', 'team'],
    content: `I thrive in technology-focused teams that respect how people actually
work best. I'm completely technology-agnostic; I love learning new
languages and frameworks.

What really matters: genuine respect for different working styles,
recognition that async communication can be more effective than constant
meetings, complex technical problems requiring deep thinking, and
environments where technical judgment is trusted.

The best challenges let me dig deep into problems and craft robust
solutions. The worst are political ones where the constraint isn't
technical complexity but poor communication.`,
  },
  {
    key: 'thread',
    title: 'The Thread',
    keywords: ['thread', 'connecting', 'summary'],
    content: `There's a thread connecting all of it. From watching toddlers discover
how the world works at Prairie Hill, to building payment infrastructure
that handles real money, to automating away tedious processes at TaxJar,
to giving a rubber duck a brain and building an entire AI-assisted
development studio.

The thread is this: the most powerful use of technology isn't replacing
human thinking. It's amplifying our natural problem-solving processes.

I learned that at a farm school, long before I knew it would matter in
software engineering. And everything I've built since has been an attempt
to prove it.`,
  },
]
